// Global packages and components
import { Link } from '@remix-run/react'
import { Slider, Heading, Image } from '~/components'
import { getLinkType } from '~/utils'

// Types
import type { SanityRefDeconstructed, SanityRefWithAlt } from '~/types'

interface BlockLogoCarouselProps {
  title: string
  items: {
    image: SanityRefWithAlt
    title: string
    slug: string
    linkType?: string
    linkReference?: SanityRefDeconstructed
    linkSlug?: string
    linkExternal?: string
  }[]
}

const LogoInner = ({ image }: { image: SanityRefWithAlt }) => (
  <div className="flex aspect-square items-center justify-center rounded-2xl bg-white p-4 md:p-5">
    {image?.asset && (
      <Image
        image={image}
        customSize={350}
        className="max-h-full max-w-full transition-transform duration-500 group-hover:scale-105"
      />
    )}
  </div>
)

// Main export
export default function BlockLogoCarousel({
  title,
  items,
}: BlockLogoCarouselProps) {
  return items && items.length !== 0 ? (
    <div className="container">
      {title && (
        <Heading
          className="mb-10 text-center"
          tag="h2"
          size="h4"
        >
          {title}
        </Heading>
      )}
      <Slider
        className="-mx-2"
        options={{
          perPage: 2,
          breakpoints: {
            768: {
              perPage: 3,
            },
            1024: {
              perPage: 4,
            },
            1280: {
              perPage: 5,
            },
            1536: {
              perPage: 6,
            },
          },
        }}
      >
        {items.map((e, i) => {
          if (!e?.image) return null

          const link = getLinkType({
            linkType: e.linkType,
            linkReference: e.linkReference,
            linkSlug: e.linkSlug,
            linkExternal: e.linkExternal,
          })

          const regex = new RegExp('^(http|https|mailto|tel)://', 'i')

          return link ? (
            regex.test(link) ? (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="group block px-2"
              >
                <LogoInner image={e.image} />
              </a>
            ) : (
              <Link
                to={link}
                className="group block px-2"
                key={i}
              >
                <LogoInner image={e.image} />
              </Link>
            )
          ) : (
            <div
              className="px-2"
              key={i}
            >
              <LogoInner image={e.image} />
            </div>
          )
        })}
      </Slider>
    </div>
  ) : null
}
